import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const subjects = [
  'Reportage photographique',
  'Realisation audiovisuelle',
  'Conference',
  'Location d\'exposition',
  'Cession de droits d\'images',
  'Autre',
];

function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: [0.23, 1, 0.32, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', organization: '', subject: subjects[0], message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError('Veuillez remplir les champs obligatoires.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '9f4d14d4-78e5-423d-97cb-960719d5d9f2',
          from_name: 'Image & Rivière - Site web',
          subject: `[Contact Site] ${form.subject}`,
          name: form.name,
          email: form.email,
          phone: form.phone,
          organization: form.organization,
          sujet: form.subject,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSent(true);
      } else {
        setError('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch {
      setError('Erreur lors de l\'envoi. Veuillez réessayer.');
    }
    setSending(false);
  };

  return (
    <main data-testid="contact-page">
      {/* Hero */}
      <section className="relative h-[35vh] overflow-hidden bg-[#0D2B3E]">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #2B7BA8 0%, transparent 50%), radial-gradient(circle at 70% 50%, #5AACE0 0%, transparent 50%)' }} />
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-12 z-10 px-6">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-5xl md:text-6xl text-white text-center tracking-tight mb-3">
            Parlons de votre projet
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="font-lato text-lg text-[#8CA1B0] text-center">
            Je suis disponible pour tout renseignement ou demande de devis
          </motion.p>
        </div>
      </section>

      {/* Contact section */}
      <section className="py-24 bg-[#0A1A27]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Left info */}
            <FadeIn className="lg:col-span-2">
              <div className="space-y-8">
                <div>
                  <h2 className="font-montserrat text-xs uppercase tracking-[0.3em] text-[#7DD4F5] mb-6">Coordonnées</h2>
                  <div className="space-y-4">
                    <a href="tel:+33647786790" className="flex items-center gap-4 group">
                      <div className="w-12 h-12 rounded-xl bg-[#2B7BA8]/15 flex items-center justify-center group-hover:bg-[#2B7BA8]/25 transition-colors">
                        <Phone size={18} className="text-[#7DD4F5]" />
                      </div>
                      <div>
                        <p className="font-lato text-xs text-[#8CA1B0]">Téléphone</p>
                        <p className="font-lato text-sm text-white">06.47.78.67.90</p>
                      </div>
                    </a>
                    <a href="mailto:contact@image-riviere.com" className="flex items-center gap-4 group">
                      <div className="w-12 h-12 rounded-xl bg-[#2B7BA8]/15 flex items-center justify-center group-hover:bg-[#2B7BA8]/25 transition-colors">
                        <Mail size={18} className="text-[#7DD4F5]" />
                      </div>
                      <div>
                        <p className="font-lato text-xs text-[#8CA1B0]">Email</p>
                        <p className="font-lato text-sm text-white">contact@image-riviere.com</p>
                      </div>
                    </a>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#2B7BA8]/15 flex items-center justify-center">
                        <MapPin size={18} className="text-[#7DD4F5]" />
                      </div>
                      <div>
                        <p className="font-lato text-xs text-[#8CA1B0]">Localisation</p>
                        <p className="font-lato text-sm text-white">France</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="glass-light rounded-2xl p-6">
                  <div className="flex items-start gap-4">
                    <img src="https://customer-assets.emergentagent.com/job_template-astud/artifacts/p2firt48_Auteur-2-scaled.webp" alt="Yannick" className="w-16 h-16 rounded-xl object-cover object-top" />
                    <div>
                      <p className="font-playfair italic text-sm text-[#F5F7F9] leading-relaxed">
                        "Chaque projet commence par une conversation. N'hésitez pas à me contacter."
                      </p>
                      <p className="font-montserrat text-xs text-[#7DD4F5] mt-2">Yannick Gouguenheim</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Form */}
            <FadeIn delay={0.2} className="lg:col-span-3">
              {sent ? (
                <div className="glass-light rounded-2xl p-12 text-center" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={32} className="text-green-400" />
                  </div>
                  <h3 className="font-playfair text-2xl text-white mb-3">Message envoyé !</h3>
                  <p className="font-lato text-[#8CA1B0]">Je vous répondrai dans les meilleurs délais.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="glass-light rounded-2xl p-8" data-testid="contact-form">
                  <h3 className="font-montserrat text-xs uppercase tracking-[0.3em] text-[#7DD4F5] mb-6">Votre demande</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="font-lato text-xs text-[#8CA1B0] mb-1.5 block">Votre nom *</label>
                      <input name="name" value={form.name} onChange={handleChange} className="form-input" placeholder="Votre nom" data-testid="contact-name" />
                    </div>
                    <div>
                      <label className="font-lato text-xs text-[#8CA1B0] mb-1.5 block">Votre email *</label>
                      <input name="email" type="email" value={form.email} onChange={handleChange} className="form-input" placeholder="Votre adresse email" data-testid="contact-email" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="font-lato text-xs text-[#8CA1B0] mb-1.5 block">Téléphone</label>
                      <input name="phone" value={form.phone} onChange={handleChange} className="form-input" placeholder="06 XX XX XX XX" data-testid="contact-phone" />
                    </div>
                    <div>
                      <label className="font-lato text-xs text-[#8CA1B0] mb-1.5 block">Organisation</label>
                      <input name="organization" value={form.organization} onChange={handleChange} className="form-input" placeholder="Votre structure" data-testid="contact-organization" />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="font-lato text-xs text-[#8CA1B0] mb-1.5 block">Sujet de votre demande</label>
                    <select name="subject" value={form.subject} onChange={handleChange} className="form-input" data-testid="contact-subject">
                      {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="font-lato text-xs text-[#8CA1B0] mb-1.5 block">Votre message *</label>
                    <textarea name="message" value={form.message} onChange={handleChange} rows={5} className="form-input" placeholder="Décrivez votre projet..." data-testid="contact-message" />
                  </div>

                  {error && <p className="font-lato text-sm text-red-400 mb-4" data-testid="contact-error">{error}</p>}

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit"
                    className="w-full py-3.5 rounded-xl bg-[#2B7BA8] text-white font-montserrat text-sm uppercase tracking-wider hover:bg-[#5AACE0] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {sending ? 'Envoi en cours...' : (<>Envoyer ma demande <Send size={14} /></>)}
                  </button>
                </form>
              )}
            </FadeIn>
          </div>
        </div>
      </section>
    </main>
  );
}
